import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getMasterUnit,
  updateMasterUnit,
  getCityBranches,
  scanConsignment,
  MasterUnit,
  UpdateMasterUnitRequest,
  Branch,
  ScanConsignmentResponse,
  City
} from '../../services/masterUnitApi';
import { cityApi } from '../../services/cityApi';
import { useTheme } from '../../contexts/ThemeContext';
import GenericButton from '../../components/GenericButton';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Divider
} from '@mui/material';

const MasterUnitEdit: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [masterUnit, setMasterUnit] = useState<MasterUnit | null>(null);
  const [formData, setFormData] = useState<UpdateMasterUnitRequest>({
    seal_number: '',
    mu_type: 'Sag Bag',
    type: 'Bulky',
    destination: 0,
    inbound_branch_id: 0,
    consignmentIds: []
  });

  const [cities, setCities] = useState<City[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [consignments, setConsignments] = useState<ScanConsignmentResponse[]>([]);
  const [scanInput, setScanInput] = useState('');
  const [scanFeedback, setScanFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originCityName, setOriginCityName] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchMasterUnit();
    }
  }, [id]);

  const fetchMasterUnit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch cities and master unit data in parallel
      const [citiesResponse, data] = await Promise.all([
        cityApi.getCities({ status: '1' }),
        getMasterUnit(Number(id))
      ]);

      setCities(citiesResponse.cities);
      setMasterUnit(data);

      // Set origin city name from cities list or from masterUnit data
      const originCity = citiesResponse.cities.find(c => c.id === data.origin);
      setOriginCityName(data.originCity?.name || originCity?.name || 'Unknown');

      // Populate form data
      setFormData({
        seal_number: data.seal_number,
        mu_type: data.mu_type as 'Sag Bag' | 'Mother Bag',
        type: data.type as 'Bulky' | 'Non Bulky' | 'COD' | 'Express',
        destination: data.destination || 0,
        inbound_branch_id: data.inbound_branch_id,
        consignmentIds: data.consignments?.map(c => c.id) || []
      });

      // Load existing consignments
      if (data.consignments) {
        const existingConsignments = data.consignments.map(c => ({
          id: c.id,
          consignment_no: c.consignment_no,
          booking_type: c.booking_type,
          service_type: c.service_type,
          created_at: c.created_at,
          chargeable_weight: c.chargeable_weight,
          status: c.status?.name || 'Unknown'
        }));
        setConsignments(existingConsignments);
      }

      // Load branches for destination
      if (data.destination) {
        const branchesData = await getCityBranches(data.destination);
        setBranches(branchesData);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDestinationChange = async (destinationId: number) => {
    setFormData(prev => ({
      ...prev,
      destination: destinationId,
      inbound_branch_id: 0
    }));

    if (destinationId) {
      try {
        const branchesData = await getCityBranches(destinationId);
        setBranches(branchesData);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleScanConsignment = async () => {
    if (scanInput.length !== 17) {
      setScanFeedback('Consignment number must be 17 characters long');
      return;
    }

    try {
      const result = await scanConsignment({
        consignment_no: scanInput,
        destination_id: formData.destination || 0,
        journey_type: 'Forward', // Default for edit
        client_id: undefined
      });

      // Check if consignment is already added
      if (formData.consignmentIds.includes(result.id)) {
        setScanFeedback('Consignment already added');
        return;
      }

      // Add consignment to list
      setFormData(prev => ({
        ...prev,
        consignmentIds: [...prev.consignmentIds, result.id]
      }));

      setConsignments(prev => [...prev, result]);
      setScanFeedback('Consignment added successfully');
      setScanInput('');
    } catch (err: any) {
      setScanFeedback(err.message);
    }
  };

  const removeConsignment = (consignmentId: number) => {
    setFormData(prev => ({
      ...prev,
      consignmentIds: prev.consignmentIds.filter(id => id !== consignmentId)
    }));
    setConsignments(prev => prev.filter(c => c.id !== consignmentId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.consignmentIds.length === 0) {
      setError('Please add at least one consignment');
      return;
    }

    try {
      setUpdateLoading(true);
      setError(null);
      
      await updateMasterUnit(Number(id), formData);
      navigate('/master-units');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.seal_number && formData.mu_type && formData.type && 
           formData.destination && formData.inbound_branch_id && 
           formData.consignmentIds.length > 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !masterUnit) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2 }}>Error</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>{error}</Typography>
          <GenericButton onClick={() => navigate('/master-units')}>Back to Master Units</GenericButton>
        </Box>
      </Box>
    );
  }

  if (!masterUnit) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Master Unit Not Found</Typography>
          <GenericButton onClick={() => navigate('/master-units')}>Back to Master Units</GenericButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Edit Master Unit</Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }}>MU Number: {masterUnit.mu_number}</Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Origin" value={originCityName} disabled fullWidth />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Destination" select SelectProps={{ native: true }} value={formData.destination} onChange={(e) => handleDestinationChange(Number(e.target.value))} fullWidth>
                  <option value={0}>Select Destination</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Branch" select SelectProps={{ native: true }} name="inbound_branch_id" value={formData.inbound_branch_id} onChange={handleInputChange} fullWidth>
                  <option value={0}>Select Branch</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Seal Number" name="seal_number" value={formData.seal_number} onChange={handleInputChange} fullWidth />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="MU Type" name="mu_type" select SelectProps={{ native: true }} value={formData.mu_type} onChange={handleInputChange} fullWidth>
                  <option value="Sag Bag">Sag Bag</option>
                  <option value="Mother Bag">Mother Bag</option>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField label="Type of Shipment" name="type" select SelectProps={{ native: true }} value={formData.type} onChange={handleInputChange} fullWidth>
                  <option value="Bulky">Bulky</option>
                  <option value="Non Bulky">Non Bulky</option>
                  <option value="COD">COD</option>
                  <option value="Express">Express</option>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

          {/* Consignment Scanner */}
        {formData.destination && (
          <Card sx={{ bgcolor: theme.palette.background.paper }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Scan Consignments ({formData.consignmentIds.length})</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField placeholder="Enter 17-digit consignment number" value={scanInput} onChange={(e) => setScanInput(e.target.value)} fullWidth inputProps={{ maxLength: 17 }} />
                <Button variant="contained" onClick={handleScanConsignment}>Scan</Button>
              </Stack>
              {scanFeedback && (
                <Alert severity={scanFeedback.includes('successfully') ? 'success' : 'error'} sx={{ mb: 2 }}>{scanFeedback}</Alert>
              )}
              {consignments.length > 0 && (
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Consignment #</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Booking Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Service Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Booking Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Weight (Grams)</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consignments.map((consignment) => (
                        <TableRow key={consignment.id}>
                          <TableCell>{consignment.consignment_no}</TableCell>
                          <TableCell>{consignment.booking_type}</TableCell>
                          <TableCell>{consignment.service_type}</TableCell>
                          <TableCell>{new Date(consignment.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>{consignment.chargeable_weight}</TableCell>
                          <TableCell>{consignment.status}</TableCell>
                          <TableCell>
                            <Button color="error" onClick={() => removeConsignment(consignment.id)}>Remove</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Submit Button */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <GenericButton variant="outline" type="button" onClick={() => navigate('/master-units')}>Cancel</GenericButton>
          <GenericButton type="submit" disabled={!isFormValid() || updateLoading}>{updateLoading ? 'Updating...' : 'Update Master Unit'}</GenericButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default MasterUnitEdit;
